<template>
  <div>
    <AppLoader
      v-if="loading"
      modal
    />
    <main
      v-if="!activationRequired && coa.moverType"
      class="u-container"
    >
      <header>
        <h1
          id="review-heading"
          class="font-bold text-2xl"
        >
          <!-- EMF, edit and cancel allowed -> 'View, Edit, Cancel or Extend Your Change-of-Address Order' -->
          <!-- Edit and cancel allowed -> 'View, Edit or Cancel Your Change-of-Address Order' -->
          <!-- Just cancel allowed -> 'View or Cancel Your Change-of-Address Order' -->
          <template v-if="flagEmfEnabled && coa.extInfo && coa.extInfo.eligible">
            View, Edit, or Extend Your Change-of-Address Order
          </template>
          <template v-else-if="allowEdit && flagEditable">
            View, Edit or Cancel Your Change-of-Address Order
          </template>
          <template v-else-if="militaryAddress">
            View or Cancel Your Change-of-Address Order
          </template>
          <template v-if="!allowEdit && !militaryAddress">
            View Your Change-of-Address Order
          </template>
        </h1>
      </header>
      <!-- COAC-M manager edit banner - This should be the top most banner -->
      <Banner
        v-if="coa.managerEdit"
        id="banner-manager-edit"
        class="mt-2"
      >
        <span class="font-bold">
          This Change-of-Address order was modified by your local Postal Office due to problems with the old or new address that was originally submitted. Changes to the old or new address following a correction are discouraged as such changes may delay or disrupt the delivery of your mail. Changes to email, phone, forwarding date, type of move or who's moving are encouraged.
        </span>
      </Banner>
      <!-- EMF API error banner -->
      <Banner
        v-if="emfProcessingError"
        id="banner-emf-error"
        class="mt-2"
      >
        <p class="font-bold text-lg text-blue-200">
          Error: Your Extended Mail Forwarding Has NOT Been Updated
        </p>
        <p class="font-bold mt-2">
          An error has occurred. Your mail forwarding extension has not been completed, and you will be issued an automatic refund. Please try again by submitting a new extended mail forwarding request.
        </p>
      </Banner>
      <!-- Success banner -->
      <Banner
        v-if="savedChanges || emfSaved"
        id="banner-success"
        class="mt-2"
      >
        <p
          id="changes-saved"
          class="font-bold text-lg text-blue-200"
        >
          Your Changes Have Been Saved
        </p>
        <!-- Extension success messaging -->
        <p
          v-if="emfSaved"
          class="mt-2"
        >
          Your mail forwarding has been updated. Your mail will be forwarded to your new address until {{ dateFormat(coa.extInfo.lastForwardDate, 'MM/dd/yyyy') }}.
        </p>
        <!-- Successful COA save messaging -->
        <p
          v-else
          class="mt-2"
        >
          You will receive a confirmation email shortly. Depending on the original mail forwarding start date of your order, your Official Change-of-Address Confirmation that arrives by mail may not reflect your updated information. If you already received your Official Change-of-Address Confirmation in the mail, then you will not receive another one.
        </p>
      </Banner>
      <Banner
        v-if="savedChanges && coa.medUpdated"
        id="banner-success"
        class="mt-2"
      >
        <p>
          Your mail forwarding start date has been updated to <strong>{{ dateFormat(coa.startDate) }}</strong>
        </p>
      </Banner>
      <!-- EMF advert banner -->
      <Banner
        v-if="showEmfAdvert"
        id="banner-emf-advert"
        class="mt-2"
      >
        <p class="font-bold text-lg text-blue-200">
          Extend Your Mail Forwarding Today
        </p>
        <p class="mt-2">
          By the way, you only get 12 months of free mail forwarding. Make sure your mail continues to be forwarded to your new address beyond the 12 months. Extend your Mail Forwarding End Date now for an additional cost.
        </p>
        <p class="mt-2">
          Would you like to purchase Extended Mail Forwarding now?
        </p>
        <div class="flex flex-col mt-6">
          <router-link
            id="emf-banner-yes"
            :to="{ name: 'extended-mail-forwarding' }"
            class="c-btn c-btn--ghost-blue"
          >
            Yes
          </router-link>
          <button
            id="emf-banner-no"
            type="button"
            class="c-btn mt-6"
            @click="emfAdvertDismissed = true;"
          >
            No
          </button>
        </div>
      </Banner>
      <!-- Old address CMRA banner -->
      <Banner
        v-if="cmraError"
        id="banner-old-address-cmra"
        class="mt-2"
      >
        <p class="font-bold text-lg text-blue-20">
          Your Changes Were NOT Saved
        </p>
        <p
          id="cmra-copy"
          class="mt-2"
        >
          Your edit could not be saved since mail from a commercial mail receiving agency (CMRA) is not forwarded through the USPS. The CMRA customer may make special arrangements for the CMRA operator to re-mail the mail with payment of new postage. A CMRA must accept and re-mail mail to former customers for at least 6 months after termination of the agency relationship. After the 6-month period, the CMRA may refuse mail addressed to a former customer. The Application for Delivery of Mail through Agent (PS Form 1583) requires an addressee and agent to comply with all applicable postal rules and regulations relative to delivery of mail through an agent. For more information on mail forwarding regulations, contact your local Postmaster.
        </p>
      </Banner>
      <!-- COA Processing -->
      <ErrorAlert
        v-if="coa.processing && !activationSuccess"
        id="alert-coa-processing"
        class="mt-3"
      >
        We are unable to modify your Change-of-Address at this time because it is still being processed. Please come back after 72 hours.
      </ErrorAlert>
      <div
        v-if="activationSuccess"
        id="alert-activation-success"
        class="mt-4 activation-success-message"
      >
        <img
          alt=""
          loading="lazy"
          src="@/assets/green-checkmark.svg"
        >
        <span
          id="alert-activation-success-text"
        >
          Your Change-of-Address order has been activated. There is no more action required.
        </span>
      </div>
      <!-- API save error on edit -->
      <ErrorAlert
        v-if="apiException && apiMessage"
        id="alert-api-save-error"
        class="mt-4"
      >
        {{ apiMessage }}
      </ErrorAlert>
      <!-- End of Day EOD -->
      <ErrorAlert
        v-if="coa.eodRunning"
        id="alert-end-of-day"
        class="mt-3"
      >
        Your current order is being processed, please return in 1 hour to modify or cancel it.
      </ErrorAlert>
      <!-- COA Edit Canceled -->
      <ErrorAlert
        v-if="editCanceled"
        id="alert-coa-edit-canceled"
        class="mt-3"
      >
        Your changes were NOT saved.
      </ErrorAlert>
      <!-- AMS Corrections - user will need to use corrected ZIP for future login attempts -->
      <ErrorAlert
        v-if="amsCorrectedNewZip"
        id="alert-ams-corrections-new-zip"
        class="mt-3"
      >
        The New Address ZIP Code™ that you entered has been corrected to the ZIP Code™ found in our address database. Please use the corrected ZIP Code™ for search purposes.
      </ErrorAlert>
      <!-- COA Not canceled -->
      <ErrorAlert
        v-if="coaNotCanceled"
        id="alert-coa-not-canceled"
        class="mt-3"
      >
        Your Change-of-Address has NOT been canceled.
      </ErrorAlert>
      <!-- COA cancel allowed -->
      <p
        v-if="allowCoaCancel"
        class="mt-3"
      >
        Certain elements of your Change-of-Address may not be editable on this system. If you need to change those elements, you will need to cancel this Change-of-Address and submit a new Change-of-Address with the correct information.
      </p>
      <div class="c-hairline mt-6" />
      <section class="mt-6">
        <Tooltip>
          <h2 class="font-bold text-xl">
            Move Information
          </h2>
          <template #tip>
            <p class="font-bold">
              More Information:
            </p>
            <p class="mt-3">
              If you need to modify elements of your order that are not allowed, you will need to cancel your Change-of-Address Order and file a new one.
            </p>
            <p class="font-bold mt-3">
              You are able to make some of the following updates:
            </p>
            <ul class="mt-3">
              <li>Change your email address and receive reminders of mail forwarding expiration dates</li>
              <li>Change your phone number</li>
              <!-- if hydrated coa is family filer -->
              <li v-if="coa.moverType === 'FAMILY'">
                Switch from a family move to an individual move
              </li>
              <!-- if hydrated coa is temp -->
              <li v-if="coa.forwardType === 'TEMPORARY'">
                Switch from a temporary move to a permanent move
              </li>
              <!-- if start date can be changed -->
              <li v-if="startDateEditable">
                Change your mail forwarding start date
              </li>
              <!-- if hydrated coa is temp and end date can be changed -->
              <li v-if="coa.forwardType === 'TEMPORARY' && endDateEditable">
                Change your mail forwarding end date
              </li>
              <li>Cancel your Change-of-Address order altogether</li>
              <li>If the change you want to make isn't available, you'll be able to cancel the original request and file a new Change-of-Address after you log in.</li>
            </ul>
          </template>
          <template #accessible>
            More information on allowed updates
          </template>
        </Tooltip>
        <div class="mt-4">
          <CoaItem label="Processed On">
            {{ dateFormat(coa.processedOnDate, 'LLLL dd, yyyy') }}
          </CoaItem>
          <CoaItem
            :label="coa.moverType === 'BUSINESS' ? 'Business Name': 'Name'"
            class="mt-4"
          >
            <template v-if="coa.moverType != 'BUSINESS'">
              {{
                [
                  coa.mover.firstName,
                  coa.mover.middleName,
                  coa.mover.lastName,
                  coa.mover.suffix,
                ].join(' ')
              }}
            </template>
            <template v-if="coa.moverType === 'BUSINESS'">
              {{ coa.businessName }}
            </template>
          </CoaItem>
          <CoaItem
            label="Who's Moving"
            class="mt-4"
          >
            {{ coa.moverType }}
          </CoaItem>
          <CoaItem
            label="Email Address"
            class="mt-4"
          >
            {{ coa.mover.email }}
          </CoaItem>
          <CoaItem
            label="Phone Number"
            class="mt-4"
          >
            {{ phoneFormat(coa.mover.homePhone || coa.mover.mobilePhone) }}
          </CoaItem>
          <CoaItem
            label="Phone Type"
            class="mt-4"
          >
            {{ coa.mover.homePhone ? 'HOME' : 'MOBILE' }}
          </CoaItem>
          <CoaItem
            label="Type of Move"
            class="mt-4"
          >
            {{ coa.forwardType }}
          </CoaItem>
          <CoaItem
            id="start-date"
            label="Requested Start Forwarding Date"
            class="mt-4"
          >
            {{ dateFormat(coa.startDate) }}
          </CoaItem>
          <CoaItem
            v-if="coa.forwardType === 'TEMPORARY'"
            label="Requested End Forwarding Date"
            class="mt-4"
          >
            {{ dateFormat(coa.endDate) }}
          </CoaItem>
          <CoaItem
            v-if="coa.extInfo.numExtensions"
            label="Additional Months of Mail Forwarding Requested"
            class="mt-4"
          >
            {{ coa.extInfo.numExtensions * 6 }} Months
          </CoaItem>
        </div>
      </section>
      <div class="c-hairline mt-6" />
      <section class="mt-6">
        <h2 class="font-bold text-xl">
          Old Address
        </h2>
        <CoaItem class="mt-4">
          {{ coa.oldAddress.streetAddress }}
          <br>{{ coa.oldAddress.city }} {{ coa.oldAddress.state }} {{ zip9Format(coa.oldAddress.zip9) }}
          <br v-if="coa.oldAddress.urbanizationName">{{ coa.oldAddress.urbanizationName || '' }}
        </CoaItem>
      </section>
      <div class="c-hairline mt-6" />
      <section class="mt-6">
        <h2 class="font-bold text-xl">
          New Address
        </h2>
        <CoaItem class="mt-4">
          {{ coa.newAddress.streetAddress }}
          <br>{{ coa.newAddress.city }} {{ coa.newAddress.state }} {{ zip9Format(coa.newAddress.zip9) }}
          <br v-if="coa.newAddress.urbanizationName">{{ coa.newAddress.urbanizationName || '' }}
        </CoaItem>
      </section>
      <div
        v-if="!coaUnEditableAndProcessing"
        class="flex flex-col mt-8"
      >
        <router-link
          v-if="allowEdit && flagEditable"
          id="edit-order"
          :to="{ name: 'edit' }"
          class="c-btn"
          @click.native="analytics.click.editMyOrder()"
        >
          Edit My Order
        </router-link>
        <router-link
          v-if="(allowEdit && allowCoaCancel) || militaryAddress"
          id="cancel-coa"
          :to="{ name: 'cancel' }"
          class="c-btn c-btn--secondary mt-6"
          @click.native="analytics.click.cancelMyOrder()"
        >
          Cancel My Order
        </router-link>
        <router-link
          v-if="coa.extInfo.eligible"
          id="extend-mail-forwarding"
          :to="{ name: 'extended-mail-forwarding' }"
          class="c-btn c-btn--secondary mt-6"
          @click.native="analytics.click.extendMailForwarding()"
        >
          Extend Mail Forwarding
        </router-link>
      </div>
      <!-- Hiding VoterReg, uncomment to bring it back -->
      <!-- <VoterReg
        v-show="showVoterReg"
        class="mt-6"
        @handoff="handoff"
      /> -->
    </main>
    <!-- activation modal-->
    <Activation
      v-if="activationRequired"
      :activation-failed="activationFailed"
      @close="activationRequired = false, refreshCoa()"
    />
  </div>
</template>

<script>
import {
  AppLoader,
  Banner,
  CoaItem,
  ErrorAlert,
  Tooltip,
} from '@mgo/ui/lib/components';
import { mobileAnalytics } from './analytics';
import reviewMixin from './mixin';
import Activation from '../Activation/index.vue';

export default {
  name: 'Review',
  components: {
    AppLoader,
    Banner,
    CoaItem,
    ErrorAlert,
    Tooltip,
    Activation,
  },
  mixins: [reviewMixin],
  computed: {
    analytics() {
      return mobileAnalytics();
    },
  },
};
</script>
